import Vue from 'vue';
import Router from 'vue-router';
import qs from 'qs';
import User from './models/User';
import LearningUnit from './models/LearningUnit';
import { useAuth } from './composables/useAuth';
// import { useSessionManager } from "./composables/useSessionManager";
import { Auth } from './models/Auth';
import NProgress from 'nprogress';
import { routes } from './configs/route-config';
import { isFeatureEnabled } from './mixins/optimizelyMixin';
// const sessionManager = useSessionManager();

Vue.use(Router);

const router = new Router({
  mode: 'history',

  base: __dirname,

  scrollBehavior(to) {
    if (to.hash) {
      return { selector: to.hash, behavior: 'smooth' };
    }
    return { x: 0, y: 0, behavior: 'smooth' };
  },

  /**
   * allow vue route query with nested object
   * https://github.com/vuejs/vue-router/issues/1259
   */
  parseQuery(query) {
    return qs.parse(query);
  },

  stringifyQuery(query) {
    var result = qs.stringify(query);

    return result ? '?' + result : '';
  },

  routes
});

router.beforeEach(async (to, from, next) => {
  console.log('[router.js] beforeEach hook is called [to, from]', [to, from]);

  // CloudFront support: Force full page reload for specific routes
  // This prevents the flash of content by reloading BEFORE Vue processes the route
  if (from.name && to.path) {
    const routeNamesNeedingReload = [
      'learning-unit', // Home page (/)
      'badge-collection', // Badge collection page
      'user-dashboard', // User dashboard page
      'learning-unit/method-components', // /micro-credentials/:id/method-components
      'learning-unit/references', // /micro-credentials/:id/references
      'learning-unit/method-components-preview', // /micro-credentials/:id/method-components/preview
      'learning-unit/references-preview', // /micro-credentials/:id/references/preview
      'design-planning',
      'design-planning/method-components', // /design-planning/:id/method-components
      'stack-managing',
      'evaluate'
    ];

    // Check if route name matches or path starts with language prefix
    const needsReload =
      routeNamesNeedingReload.includes(to.name) ||
      to.path == '/en' ||
      to.path == '/th' ||
      to.path.startsWith('/en/') ||
      to.path.startsWith('/th/');

    if (needsReload) {
      window.location.href = to.fullPath + '?t=' + Date.now(); //To prevent browser cache
      return; // Stop navigation, let the browser handle the reload
    }
  }

  NProgress.start();

  NProgress.set(0.1);

  const {
    authorize,
    learningUnit,
    application,
    authenticated = true,
    isPublic = false,
    featureFlag
  } = to.meta;

  const { initializeAuth, getCurrentFirebaseUser } = useAuth();

  const firebaseUser = await getCurrentFirebaseUser();

  console.log('[router.js] check firebase auth user', firebaseUser);

  // check firebase auth user existence, if it exists, mark token invalid to force user to re-sign in
  if (firebaseUser) {
    Auth.markTokenInvalid();

    return;
  }

  const isAuthInitilized = Auth.isInitialized();

  if (!isAuthInitilized) {
    try {
      await initializeAuth();
    } catch (error) {
      console.log('[router.js] error initializing auth', error);

      Auth.markTokenInvalid();

      return;
    }
  }

  const authUser = Auth.getCurrentUser();

  // if (authUser && to.query.redirectUrl) {
  //   return location.assign(decodeURIComponent(to.query.redirectUrl));
  // }

  if (isPublic) {
    return next();
  }

  if (authenticated && !authUser) {
    return next({
      path: '/login',
      query: {
        previousPath: to.fullPath
      }
    });
  }

  if (!authenticated && authUser) {
    window.location.replace('/');
    return;
  }

  if (authorize) {
    const user = User.query().with('user_roles').first();

    // check if route is restricted by role
    if (authorize.length) {
      if (authorize.includes('*')) {
        return next();
      } else {
        const checkIsUserAuthorize = user.user_roles.some(({ role } = {}) => {
          return authorize.includes(role);
        });
        console.log('[router.js] checkIsUserAuthorize', checkIsUserAuthorize);
        // role not authorised so redirect to LU page
        if (!checkIsUserAuthorize) {
          console.log('[router.js] cannot navigate: role is not authorized.');

          return next({ path: '/not-found' });
        }
      }

      let isLearningUnitMember = false;
      let isApplied = false;

      if (learningUnit) {
        const { learningUnitId } = to.params;

        if (!learningUnitId) {
          throw new Error("route must defined 'learningUnitId' in params");
        }

        isLearningUnitMember = user.isLearningUnitMemberOf(+learningUnitId);

        if (learningUnit.isDraftOnly) {
          const {
            response: {
              data: { isDraft }
            }
          } = await LearningUnit.api().checkMcIsDraft(learningUnitId);

          if (!isDraft) {
            console.log('[router.js] access denied: learning unit is not in draft status.');

            return next({ path: '/not-found' });
          }
        }
      }

      // If not learning unit member, but have meta `application` => authorized by application mode
      if (!isLearningUnitMember && application && application.mode) {
        const { learningUnitId } = to.params;
        const { applications = [] } = user;

        isApplied = applications.some(
          (apply) =>
            apply.user == user.id &&
            apply.learning_unit == learningUnitId &&
            apply.mode == application.mode
        );
      }

      // check only is learning unit member
      if (!application && learningUnit && learningUnit.isMember && !isLearningUnitMember) {
        console.log('[router.js] access denied: user must be a member of this learning unit.');

        return next({ path: '/not-found' });
      }

      // check only must apply
      if (!learningUnit && application && application.mode && !isApplied) {
        console.log('[router.js] access denied: user must apply to this learning unit.');

        return next({ path: '/not-found' });
      }

      // check both is member and must apply
      if (
        application &&
        learningUnit &&
        application.mode &&
        learningUnit.isMember &&
        !isLearningUnitMember &&
        !isApplied
      ) {
        console.log(
          '[router.js] access denied: user must be a member or must apply to this learning unit.'
        );

        return next({ path: '/not-found' });
      }
    }
  }

  if (featureFlag) {
    if (featureFlag.length) {
      if (featureFlag.includes('*')) {
        return next();
      } else {
        const username = authUser ? authUser.username : 'unauthenticated';
        const isAuthenticated = !!authUser;
        const featureEnabled = featureFlag.every((f) => {
          return isFeatureEnabled(Vue.$optimizelyClientInstance, f, username, { isAuthenticated });
        });

        if (!featureEnabled) {
          console.log('[router.js] access denied: feature is not enabled.');

          return next({ path: '/not-found' });
        }
      }
    }
  }

  return next();
});

router.afterEach(async (to, from) => {
  Vue.$amplitudeClient.logEvent('Page Viewed', {
    fromPath: from.fullPath,
    toPath: to.fullPath
  });

  setTimeout(() => NProgress.done(), 500);
});

export default router;
